import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Plus, Store, ChevronRight } from "lucide-react";
import { Button, Badge, EmptyState, buttonVariants } from "@/components/ui";
import {
  ListToolbar,
  ErrorState,
  ListResultsCard,
} from "@/components/common";
import { AdminStatusFilter } from "@/components/admin/AdminStatusFilter";
import {
  countByStatusFilter,
  matchesStatusFilter,
  type StatusFilter,
} from "@/components/admin/adminListStatus";
import type { ActivityWithConfig } from "@/services/adminService";
import { ActivityCategory } from "@/types";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import { matchesTextQuery } from "@/lib/textSearch";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import { cn } from "@/lib/utils";

interface AdminUserActivitiesSectionProps {
  ownerUserId: string;
  activities: ActivityWithConfig[] | undefined;
  isLoading: boolean;
  error: boolean;
  canCreate: boolean;
  title: string;
  loadMessage: string;
  errorMessage: string;
  emptyTitle: string;
  emptyDescription: string;
  ownerContext?: "seller" | "host";
}

function ActivityRow({ activity }: { activity: ActivityWithConfig }) {
  const { t: tShared, i18n } = useTranslation("adminUsers");
  const { t } = useTranslation("sellers");
  const { t: tActivities } = useTranslation("activities");
  const activityName = resolveCatalogTranslation(
    activity.name_translations,
    i18n.language,
  );

  return (
    <Link
      to={`/activities/${activity.id}`}
      className="group hover:bg-surface-50 dark:hover:bg-surface-100/50 flex items-center justify-between gap-4 px-5 py-3 transition-colors"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
            activity.category === ActivityCategory.RESTAURANT
              ? "bg-warning-50 text-warning-600 ring-warning-600/20"
              : "bg-primary-50 text-primary-600 ring-primary-600/15",
          )}
        >
          <Store
            className="h-4 w-4"
            aria-hidden
          />
        </div>
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h4 className="text-surface-900 truncate text-sm font-medium">
              {activityName}
            </h4>
            <Badge
              variant={activity.is_active ? "success" : "default"}
              size="sm"
              dot
            >
              {activity.is_active
                ? tShared("list.statusActive")
                : tShared("list.statusInactive")}
            </Badge>
            {activity.is_active && !activity.is_visible ? (
              <Badge
                variant="default"
                size="sm"
              >
                {tActivities("card.hidden")}
              </Badge>
            ) : null}
          </div>
          <p className="text-surface-500 mt-0.5 text-[13px]">
            {activity.category === ActivityCategory.RESTAURANT
              ? t("detail.categoryRestaurant")
              : activity.category === ActivityCategory.GENERIC_SERVICE
                ? t("detail.categoryService")
                : t("detail.categoryAttraction")}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-4">
        <div className="text-right">
          <p className="text-surface-500 text-xs">
            {t("detail.activityCommission")}
          </p>
          <p className="text-surface-900 text-sm font-semibold tabular-nums">
            {formatCommissionPercentLabel(activity.commission_rate)}
          </p>
        </div>
        <ChevronRight
          className="text-surface-400 h-4 w-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>
    </Link>
  );
}

export function AdminUserActivitiesSection({
  ownerUserId,
  activities,
  isLoading,
  error,
  canCreate,
  title,
  loadMessage,
  errorMessage,
  emptyTitle,
  emptyDescription,
  ownerContext = "seller",
}: AdminUserActivitiesSectionProps) {
  const { t, i18n } = useTranslation("adminUsers");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const createHref =
    ownerContext === "host"
      ? `/admin/create-activity?owner_user_id=${ownerUserId}&context=host`
      : `/admin/create-activity?owner_user_id=${ownerUserId}`;

  const searchMatched = useMemo(() => {
    if (!activities) return [];
    if (!searchQuery.trim()) return activities;
    return activities.filter((activity) =>
      matchesTextQuery(searchQuery, [
        resolveCatalogTranslation(activity.name_translations, i18n.language),
      ]),
    );
  }, [activities, searchQuery, i18n.language]);

  const filteredActivities = useMemo(
    () =>
      searchMatched.filter((activity) =>
        matchesStatusFilter(activity.is_active, statusFilter),
      ),
    [searchMatched, statusFilter],
  );

  const statusFilterCounts = useMemo(
    () => countByStatusFilter(searchMatched, (activity) => activity.is_active),
    [searchMatched],
  );

  const hasActivities = (activities?.length ?? 0) > 0;
  const filtersNarrowed = searchQuery.trim() !== "" || statusFilter !== "all";

  return (
    <ListResultsCard
      title={title}
      headerAction={
        canCreate ? (
          <Link to={createHref}>
            <Button
              type="button"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t("detail.addActivity")}
            </Button>
          </Link>
        ) : null
      }
      beforeContent={
        !isLoading && !error && hasActivities ? (
          <div className="border-surface-200 border-b px-5 py-3">
            <ListToolbar
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder={t("detail.searchActivitiesPlaceholder")}
              searchAccessory={
                <AdminStatusFilter
                  value={statusFilter}
                  onChange={setStatusFilter}
                  counts={statusFilterCounts}
                  labels={{
                    name: t("list.filterStatus"),
                    all: t("list.filterAll"),
                    active: t("list.filterActive"),
                    inactive: t("list.filterInactive"),
                  }}
                />
              }
            />
          </div>
        ) : null
      }
      contentClassName="p-0"
      isLoading={isLoading}
      loadingMessage={loadMessage}
      isError={!isLoading && error}
      errorState={
        <div className="p-5">
          <ErrorState message={errorMessage} />
        </div>
      }
      isEmpty={!isLoading && !error && !hasActivities}
      emptyState={
        <div className="p-5">
          <EmptyState
            icon={Store}
            title={emptyTitle}
            description={emptyDescription}
            action={
              canCreate ? (
                <Link
                  to={createHref}
                  className={buttonVariants({ variant: "outline", size: "sm" })}
                >
                  <Plus
                    className="mr-1.5 h-4 w-4"
                    aria-hidden
                  />
                  {t("detail.addActivity")}
                </Link>
              ) : undefined
            }
          />
        </div>
      }
    >
      {filteredActivities.length === 0 ? (
        <div className="p-5">
          <EmptyState
            icon={Store}
            title={t("detail.noActivitiesMatchTitle")}
            description={
              filtersNarrowed
                ? t("detail.noActivitiesMatchDesc")
                : emptyDescription
            }
          />
        </div>
      ) : (
        <div className="divide-surface-100 divide-y">
          {filteredActivities.map((activity) => (
            <ActivityRow
              key={activity.id}
              activity={activity}
            />
          ))}
        </div>
      )}
    </ListResultsCard>
  );
}
